import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export interface DueReflection {
  id: string;
  meetingId: string;
  meetingTitle: string;
  meetingStartTime: Date;
  behaviorId: string;
  behaviorName: string;
  leaderBehaviorId: string;
  reflectionPromptAvailableAt: Date;
}

const PROMPT_DISMISSED_KEY = "veera-reflection-dismissed";

function getDismissedToday(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(PROMPT_DISMISSED_KEY);
    if (!stored) return [];
    const { date, ids } = JSON.parse(stored);
    const today = new Date().toISOString().split("T")[0];
    if (date === today) return ids;
    return [];
  } catch {
    return [];
  }
}

export function dismissReflectionPrompt(summaryId: string) {
  if (typeof window === "undefined") return;
  try {
    const today = new Date().toISOString().split("T")[0];
    const current = getDismissedToday();
    if (!current.includes(summaryId)) {
      current.push(summaryId);
    }
    localStorage.setItem(PROMPT_DISMISSED_KEY, JSON.stringify({ date: today, ids: current }));
  } catch {
    // Ignore storage failures
  }
}

export function useDueReflections() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["due-reflections", user?.id],
    queryFn: async (): Promise<DueReflection[]> => {
      if (!user?.id) return [];
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return [
          {
            id: "preview-due-reflection-1",
            meetingId: "m1",
            meetingTitle: "Product Roadmap Sync",
            meetingStartTime: new Date("2026-04-08T10:00:00-07:00"),
            behaviorId: "preview-behavior-id",
            behaviorName: "Strategic Listening",
            leaderBehaviorId: "preview-leader-behavior-id",
            reflectionPromptAvailableAt: new Date("2026-04-08T11:15:00-07:00"),
          },
        ];
      }

      const now = new Date().toISOString();

      // Query from the canonical view
      const { data, error } = await supabase
        .from("v_behavior_practice_summaries")
        .select(`
          id,
          meeting_id,
          meeting_title,
          meeting_start_time,
          behavior_id,
          behavior_name,
          leader_behavior_id,
          reflection_prompt_available_at
        `)
        .eq("leader_id", user.id)
        .eq("reflection_submitted", false)
        .lte("reflection_prompt_available_at", now)
        .order("meeting_start_time", { ascending: false })
        .limit(10);

      if (error) {
        console.error("[useDueReflections] Error:", error);
        return [];
      }

      return (data || []).map((row) => ({
        id: row.id,
        meetingId: row.meeting_id,
        meetingTitle: row.meeting_title,
        meetingStartTime: new Date(row.meeting_start_time),
        behaviorId: row.behavior_id,
        behaviorName: row.behavior_name,
        leaderBehaviorId: row.leader_behavior_id,
        reflectionPromptAvailableAt: new Date(row.reflection_prompt_available_at),
      }));
    },
    enabled: !!user?.id,
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: true,
  });
}

export function useDueReflectionsCount() {
  const { data: reflections } = useDueReflections();
  return reflections?.length || 0;
}

export function useNextDueReflection() {
  const { data: reflections } = useDueReflections();
  const dismissedToday = getDismissedToday();
  
  // Return the most recent due reflection that hasn't been dismissed today
  const undismissed = reflections?.filter((r) => !dismissedToday.includes(r.id)) || [];
  return undismissed[0] || null;
}
